import { useState } from 'react';
import Image from 'next/image';

// Third-party
import clsx from 'clsx';

import { PostProvider, usePost } from '@/utils/hooks/context/usePost';
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { rgbDataURL } from '@/utils/blurImage';
// Hooks
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

import {
    getFooterStats,
    getNumberFormatOptions,
    getStatIcon,
    getStatLabel
} from '@/lib/posts/postFooterStats';
import { POST, SQUARE, STORY } from '@/lib/posts/postTypes';
// Lib
import { INSTAGRAM, YOUTUBE } from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import SocialProfileMiniDisplay from '@/components/composite/SocialProfiles/SocialProfileMiniDisplay';
import ContentIconBg from '@/components/icons/ContentIconBg';

const keystrokes = ['enter', ' ', 'space'];

const Post = ({
    data,
    isLoading = false,
    isExport = false,
    config = {
        postFormat: SQUARE,
        postType: POST,
        activeFilter: 'most_recent_post',
        network: INSTAGRAM,
        numberFormat: 'comma'
    },
    className,
    children,
    ...restProps
}) => {
    const [referenceElement, setReferenceElement] = useState();

    return (
        <PostProvider defaultValues={{ data, isLoading, config, isExport }}>
            <div
                className={clsx(
                    'relative flex flex-col items-center rounded-md',
                    { [config.postType]: isLoading },
                    className
                )}
                ref={setReferenceElement}
                {...restProps}>
                {typeof children === 'function' ? children({ referenceElement }) : children}
            </div>
        </PostProvider>
    );
};
Post.propTypes = {
    data: PropTypes.object,
    isLoading: PropTypes.bool,
    config: PropTypes.object,
    children: PropTypes.any,
    isExport: PropTypes.bool,
    className: PropTypes.string
};

//Content Wrapper
const Content = ({ children, className }) => {
    return (
        <div
            aria-label="post-content"
            className={clsx('group relative overflow-hidden rounded-md', className)}
            data-testid="post-content">
            {children}
        </div>
    );
};

Content.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string
};

Post.Content = Content;

const Collaborators = () => (
    <span className="z-1 flex h-5 w-5 items-center justify-center rounded-full bg-icoBlue">
        <Icon icon="multiprofiles" className="text-xs text-white" aria-label="collaborators" />
    </span>
);
Post.Collaborators = Collaborators;

const Promoted = () => (
    <span className="z-1 flex h-5 w-5 items-center justify-center rounded-full bg-icoBlue">
        <Icon icon="paid-data" className="text-xs text-white" aria-label="promoted" />
    </span>
);

Post.Promoted = Promoted;

const PostTypeIcon = ({ type, isExport = false }) => {
    const { isDarkMode } = useDarkMode();

    return (
        <>
            <ContentIconBg
                className="absolute -top-0.5 right-0 z-1"
                darkMode={isDarkMode() && !isExport}
            />
            <Icon
                aria-label="type-post"
                icon={type}
                className={clsx('absolute z-1 align-middle text-xxs text-icoGray-500', {
                    'right-0.5 top-0.5': !isExport,
                    'right-1 top-1.5': isExport
                })}
            />
        </>
    );
};

PostTypeIcon.propTypes = {
    type: PropTypes.string.isRequired,
    isExport: PropTypes.bool
};

Post.PostTypeIcon = PostTypeIcon;

const statusStyle = {
    default: 'bg-icoGray-100 leading-6 font-GMedium pt-5 px-5',
    album: 'bg-white leading-4 font-GMedium pt-5 px-5'
};

const statusStyleDark = {
    default: 'dark:bg-icoDarkMode-deepBlack dark:text-white',
    album: 'dark:bg-icoDarkMode-anthracite dark:text-white'
};

const Status = ({ formattedCaption, withMedia = true, remoteMedia = false, style = 'default' }) => {
    const { data, isExport } = usePost();

    return (
        <div>
            <div className="square">
                <div
                    className={clsx('relative break-words', statusStyle[style], {
                        [statusStyleDark[style]]: !isExport
                    })}>
                    {formattedCaption}
                </div>
            </div>
            {data?.medias?.length > 0 &&
            (data?.medias?.[0]?.image || data?.medias?.[0]?.thumbnail) &&
            withMedia ? (
                <div
                    className={clsx(
                        'absolute bottom-2 right-2 z-1 h-15 w-15 rounded-md bg-white p-0.5',
                        {
                            'shadow-icoS dark:bg-icoDarkMode-goudron': !isExport
                        }
                    )}>
                    <div className="relative h-full w-full overflow-hidden rounded">
                        <Img remoteMedia={remoteMedia} />
                    </div>
                </div>
            ) : null}
            <span
                className={clsx(
                    'absolute bottom-0 left-0 right-0 block h-1/3 w-full bg-gradient-to-b from-icoGray-100/0 to-icoGray-100/100',
                    {
                        'dark:from-icoDarkMode-deepBlack/0 dark:to-icoDarkMode-deepBlack/100':
                            !isExport
                    }
                )}
            />
        </div>
    );
};

Status.propTypes = {
    formattedCaption: PropTypes.node,
    withMedia: PropTypes.bool,
    remoteMedia: PropTypes.bool,
    style: PropTypes.oneOf(['default', 'album'])
};
Post.Status = Status;

//Post Image
const Img = ({ remoteMedia = false, imgFit = 'cover' }) => {
    const { isDarkMode } = useDarkMode();
    const { data, isExport } = usePost();
    const { t } = useTranslation();
    const [error, setError] = useState(false);

    if (
        error ||
        (!data?.medias?.[0]?.image && !data?.medias?.[0]?.thumbnail && !data?.medias?.[0]?.video)
    ) {
        return (
            <div
                className={clsx(
                    'flex h-full w-full flex-1 flex-col items-center justify-center space-y-4 bg-icoGray-100 text-icoGray-500',
                    { 'dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf': !isExport }
                )}>
                {/* Change Icon to real fallback */}

                <span
                    className={clsx(
                        'flex h-12 w-12 items-center justify-center rounded-full bg-icoGray-300',
                        { 'dark:bg-icoDarkMode-anthracite': !isExport }
                    )}>
                    <Icon
                        icon={data?.type === 'video' || data?.type === 'short' ? 'play' : 'albums'}
                    />
                </span>
                <p className="text-xs">
                    {data?.type === 'video'
                        ? t('commons:video_not_loaded')
                        : t('commons:media_not_loaded')}
                </p>
            </div>
        );
    }

    if (data?.medias?.[0]?.video && !data?.medias?.[0]?.thumbnail) {
        return (
            <video
                src={data?.medias?.[0]?.video}
                preload="metadata"
                className="min-h-full min-w-full"
                onError={() => setError(true)}
            />
        );
    }

    return remoteMedia ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={data?.medias?.[0]?.image ?? data?.medias?.[0]?.thumbnail}
            alt={`${data?.type} post`}
            className={`object-${imgFit} h-full w-full`}
            onError={() => setError(true)}
        />
    ) : (
        <Image
            src={data?.medias?.[0]?.image ?? data?.medias?.[0]?.thumbnail}
            alt={`${data?.type} post`}
            className="h-full w-full object-cover"
            fill
            sizes="(max-width: 350px) 100vw,
            max-width: 768px) 50vw,
              20vw"
            placeholder="blur"
            blurDataURL={isDarkMode ? rgbDataURL(5, 9, 22) : rgbDataURL(240, 242, 246)}
            priority={isExport}
            onError={() => setError(true)}
        />
    );
};

Img.propTypes = {
    remoteMedia: PropTypes.bool,
    imgFit: PropTypes.oneOf(['cover', 'contain'])
};

Post.Image = Img;

//Footer section with stats
// abstract to make number formatting easier
const StatNumber = ({ data, entry, numberFormat }) => {
    const formatOptions = getNumberFormatOptions(entry);
    const { isExport } = usePost();

    const { formatNumber } = useFormatNumber(numberFormat);
    return (
        <span className={clsx('text-xs', { 'text-icoBlack': isExport })}>
            {(data || data === 0) && data !== null ? formatNumber(data, formatOptions) : 'N/A'}
        </span>
    );
};

StatNumber.propTypes = {
    data: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    entry: PropTypes.string,
    numberFormat: PropTypes.string
};

const Footer = ({ type, feedType = '' }) => {
    const { data, config } = usePost();
    const { t } = useTranslation('commons');
    const getStats = () => {
        if (feedType === 'feed') {
            if (config?.network === INSTAGRAM) {
                return ['likes', 'comments', 'engagements'];
            }

            return ['reactions', 'comments', 'engagements'];
        }

        return getFooterStats({
            postType: config?.postType,
            type: type,
            filter: config?.activeFilter,
            network: config?.network
        });
    };
    const stats = getStats();

    return (
        <div
            aria-label="stats-section"
            className={clsx(
                'flex min-h-[3.125rem] flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-0.5 py-1',
                {
                    '!gap-5': config?.network === YOUTUBE
                }
            )}>
            {stats?.map((stat, i) => {
                if (!data[stat] && stat === 'engagement_rate') {
                    return null;
                } else {
                    const statLabel = getStatLabel(stat, config?.network);
                    return (
                        <Tooltip
                            key={i}
                            content={t(`post_footer.${stat}`)}
                            placement="top"
                            arrow={false}>
                            <span className="flex items-center space-x-2">
                                <span className="flex items-center gap-1">
                                    <Icon
                                        icon={getStatIcon(stat, config?.network)}
                                        className="text-sm text-icoBlue"
                                    />
                                    {statLabel && (
                                        <span className="font-GSemiBold text-xs">
                                            {t(`post_footer.${statLabel}`)}
                                        </span>
                                    )}
                                </span>
                                <StatNumber
                                    data={data[stat]}
                                    entry={stat}
                                    numberFormat={config?.numberFormat}
                                />
                            </span>
                        </Tooltip>
                    );
                }
            })}
        </div>
    );
};

Footer.propTypes = {
    type: PropTypes.string,
    feedType: PropTypes.string
};

Post.Footer = Footer;

// Hover section
const HoverWrapper = ({
    children,
    forceDisplay = false,
    openViewer = () => {
        /** */
    }
}) => {
    return (
        <div
            role="button"
            tabIndex="-1"
            className={clsx(
                'absolute inset-0 z-10 flex cursor-pointer flex-col items-center rounded bg-icoBlack/50 backdrop-blur-6 transition duration-300 ease-in-out',
                {
                    'opacity-0 group-hover:opacity-100': !forceDisplay
                }
            )}
            onClick={openViewer}
            onKeyDown={(e) => {
                if (keystrokes.includes(e.key.toLowerCase())) {
                    openViewer();
                }
            }}>
            {children}
        </div>
    );
};

HoverWrapper.propTypes = {
    forceDisplay: PropTypes.bool,
    openViewer: PropTypes.func,
    children: PropTypes.node
};

Post.Hover = HoverWrapper;

const Placeholder = ({ postType, isExport }) => (
    <div
        aria-label="post-placeholder"
        className={clsx(
            'relative flex w-full flex-col items-center rounded-md bg-white px-2 pb-12 pt-2',
            {
                post: postType === POST,
                story: postType === STORY,
                'dark:bg-icoDarkMode-anthracite': !isExport
            }
        )}
        data-testid="loading-placeholder">
        <div
            className={clsx('h-full w-full animate-pulse rounded-md bg-icoGray-300', {
                'dark:bg-icoDarkMode-deepBlack': !isExport
            })}
        />
    </div>
);

Placeholder.propTypes = {
    postType: PropTypes.oneOf([POST, STORY]),
    isExport: PropTypes.bool
};

Post.Placeholder = Placeholder;

// post header
const Header = ({ data, isExport = false, className }) => {
    if (!data?.username && !data?.social_profile) {
        return null;
    }

    return (
        <div
            aria-label="post-header"
            className={clsx(
                'group relative mb-2 flex w-fit max-w-[80%] flex-row items-center space-x-1 overflow-hidden font-GSemiBold text-xs text-black',
                className,
                { 'dark:text-white': !isExport }
            )}>
            {data?.social_profile ? (
                <SocialProfileMiniDisplay socialProfile={data.social_profile} isExport={isExport} />
            ) : null}
            {data?.username ? <span className="truncate">{data.username}</span> : null}
        </div>
    );
};

Header.propTypes = {
    data: PropTypes.object,
    isExport: PropTypes.bool,
    className: PropTypes.string
};

Post.Header = Header;

export default Post;
